import React, { useMemo, useState } from "react";

import { ButtonStandard, InputWrapper, ModalBase, SingleSelectInput, Text } from "@performyard/py-components";

import type { Competency } from "../../../../../api/services/defs/competencies";

interface RoleCompetencyDetail {
	competency_id: string;
	expected_level: number | null;
	name: string;
	is_company_wide: boolean;
	levels: Array<{ rank: number; name: string; description: string }>;
}

export interface CompetencyPickerModalProps {
	isOpen: boolean;
	mode: "add" | "edit";
	roleCompetencies: RoleCompetencyDetail[];
	allActiveCompetencies: Competency[];
	editTarget?: RoleCompetencyDetail;
	onSubmit: (input: { competency_id: string; expected_level: number | null }) => void;
	onClose: () => void;
}

const buildLevelOptions = (levels: Array<{ rank: number; name: string }>): Array<{ value: number; label: string }> =>
	levels.map((lvl) => ({ value: lvl.rank, label: `Level ${lvl.rank}: ${lvl.name}` }));

const CompetencyPickerModal: React.FC<CompetencyPickerModalProps> = ({
	isOpen,
	mode,
	roleCompetencies,
	allActiveCompetencies,
	editTarget,
	onSubmit,
	onClose,
}) => {
	const [selectedId, setSelectedId] = useState<string | null>(editTarget?.competency_id ?? null);
	const [selectedLevel, setSelectedLevel] = useState<number | null>(editTarget?.expected_level ?? null);
	const [showCompetencyError, setShowCompetencyError] = useState(false);

	// Reset state whenever the editTarget changes (e.g., reopening the modal for a different row).
	React.useEffect(() => {
		setSelectedId(editTarget?.competency_id ?? null);
		setSelectedLevel(editTarget?.expected_level ?? null);
		setShowCompetencyError(false);
	}, [editTarget?.competency_id, editTarget?.expected_level]);

	const addedIds = useMemo(() => new Set(roleCompetencies.map((rc) => rc.competency_id)), [roleCompetencies]);

	// Add mode: only non-CW active competencies that aren't already on the role.
	const competencyOptions = useMemo(() => {
		if (mode === "edit") return [];
		return allActiveCompetencies
			.filter((c) => !c.is_company_wide && c.status === "active" && !addedIds.has(c._id))
			.map((c) => ({ value: c._id, label: c.name }));
	}, [mode, allActiveCompetencies, addedIds]);

	const selectedCompetency: { name: string; levels: Array<{ rank: number; name: string }> } | undefined =
		mode === "edit" ? editTarget : allActiveCompetencies.find((c) => c._id === selectedId);

	const levelOptions = selectedCompetency ? buildLevelOptions(selectedCompetency.levels) : [];
	const hasLevels = levelOptions.length > 0;

	const handleSubmit = () => {
		if (mode === "add" && !selectedId) {
			setShowCompetencyError(true);
			return;
		}
		const competencyId = mode === "edit" ? editTarget?.competency_id : selectedId;
		if (!competencyId) return;
		onSubmit({ competency_id: competencyId, expected_level: selectedLevel });
	};

	const handleCompetencyChange = (v: string | null) => {
		setSelectedId(v);
		if (v) setShowCompetencyError(false);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div className="box-border w-[432px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
					{mode === "add" ? "Add Competency" : "Edit expected level"}
				</Text>

				{mode === "edit" && editTarget && (
					<Text
						size="paragraph-2"
						weight="regular"
						color="neutral-7"
						dangerouslySetClassName="text-center mt-1"
					>
						{editTarget.name}
					</Text>
				)}

				<div className="mt-4 flex flex-col gap-4">
					{mode === "add" && (
						<InputWrapper
							id="modal-picker-competency"
							label="Competency"
							error={showCompetencyError ? "Please select a competency" : undefined}
						>
							<SingleSelectInput<string>
								value={selectedId ?? undefined}
								options={competencyOptions}
								onChange={handleCompetencyChange}
								placeholder="Select a competency"
								errored={showCompetencyError}
							/>
						</InputWrapper>
					)}

					<InputWrapper id="modal-picker-level" label="Expected Level">
						{!selectedCompetency ? (
							<SingleSelectInput<number>
								value={undefined}
								options={[]}
								onChange={() => undefined}
								placeholder="Select a competency first"
								disabled
							/>
						) : hasLevels ? (
							<SingleSelectInput<number>
								value={selectedLevel ?? undefined}
								options={levelOptions}
								onChange={(v) => setSelectedLevel(v)}
								placeholder="Make a selection"
								isClearable
							/>
						) : (
							<SingleSelectInput<number>
								value={undefined}
								options={[]}
								onChange={() => undefined}
								placeholder="No levels created"
								disabled
							/>
						)}
					</InputWrapper>
				</div>

				<div className="mt-6 flex justify-center gap-2">
					<div>
						<ButtonStandard size="large" variant="outline" color="neutral" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div>
						<ButtonStandard
							size="large"
							color="primary"
							onClick={handleSubmit}
							data-testid="competency-picker-submit"
						>
							{mode === "add" ? "Add Competency" : "Save Changes"}
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default CompetencyPickerModal;
